import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  createAccessManagementUser,
  deleteUserByEmail,
  fetchActivePublishers,
  getUsersByEmail,
  getUsersByOrgId,
  editAccessManagementUser
} from '@shared/api';
import { canAccessPage, userTypes } from '@shared/lib/access';
import { getEmailFromAccessToken, getOrgIdFromAccessToken } from '@shared/lib/jwt';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import type { AccessManagementUser } from '@shared/store/userManagementSlice';
import { setUsersForOrg } from '@shared/store/userManagementSlice';
import DataTable from '@shared/ui/DataTable';
import MultiSelectInput, { type MultiSelectOption } from '@shared/ui/MultiSelectInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

type SelectOption = { label: string; value: string };
type UserEditorMode = 'create' | 'edit';

const cOrgIDOptions: SelectOption[] = [
  {
    value: 'CRACKLE-RTB',
    label: 'Crackle RTB'
  },
  {
    value: 'CRACKLE-SP',
    label: 'Crackle SP'
  }
];

interface UserEditorModalProps {
  mode: UserEditorMode;
  currentUserType: string | null;
  tokenOrgId: string | null;
  orgOptions: SelectOption[];
  demandPartnerOptions: MultiSelectOption[];
  selectedUser: AccessManagementUser | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    userName: string;
    email: string;
    userType: SelectOption;
    organization: SelectOption;
    demandPartners: SelectOption[];
    publishers: SelectOption[];
  }) => void;
}

const getAllowedUserTypeOptions = (currentUserType: string | null): SelectOption[] => {
  switch (currentUserType) {
    case userTypes.Crackle_Admin:
      return [
        { label: 'Crackle Admin', value: userTypes.Crackle_Admin },
        { label: 'Crackle User', value: userTypes.Crackle_User },
        { label: 'Demand Partner Admin', value: userTypes.Demand_Partner_Admin },
        { label: 'Supply Partner Admin', value: userTypes.Supply_Partner_Admin },
      ];
    case userTypes.Demand_Partner_Admin:
      return [
        { label: 'Demand Partner Admin', value: userTypes.Demand_Partner_Admin },
        { label: 'Demand Partner User', value: userTypes.Demand_Partner_User }
      ];
    case userTypes.Supply_Partner_Admin:
      return [
        { label: 'Supply Partner Admin', value: userTypes.Supply_Partner_Admin },
        { label: 'Supply Partner Tech', value: userTypes.Supply_Partner_Tech },
        { label: 'Supply Partner Ops', value: userTypes.Supply_Partner_Ops }
      ];
    default:
      return [];
  }
};

function UserEditorModal({
  mode,
  currentUserType,
  tokenOrgId,
  orgOptions,
  demandPartnerOptions,
  selectedUser,
  isSaving,
  onClose,
  onSave
}: UserEditorModalProps) {
  const userTypeOptions = useMemo(() => getAllowedUserTypeOptions(currentUserType), [currentUserType]);
  const [selectedType, setSelectedType] = useState<SelectOption | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<SelectOption | null>(null);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedDemandPartners, setSelectedDemandPartners] = useState<MultiSelectOption[]>([]);
  const [selectedPublishers, setSelectedPublishers] = useState<MultiSelectOption[]>([]);
  const isFixedOrgType =
    selectedType?.value === userTypes.Crackle_Admin ||
    selectedType?.value === userTypes.Crackle_User ||
    selectedType?.value === userTypes.Supply_Partner_Admin;

  const orgSelectDisabled =
    mode === 'edit' ||
    currentUserType !== userTypes.Crackle_Admin ||
    isFixedOrgType ||
    !selectedType

  const showDemandPartnerList = selectedType?.value === userTypes.Crackle_User;
  const showPublisherList = selectedType?.value === userTypes.Supply_Partner_Ops;
  const { data: publisherData = [], isLoading: isLoadingPublishers } = useQuery({
    queryKey: ['activePublishers'],
    queryFn: fetchActivePublishers,
    enabled: showPublisherList
  });
  const publisherOptions = useMemo<MultiSelectOption[]>(
    () =>
      publisherData.map((publisher) => ({
        label: publisher.pubName,
        value: publisher.pubId
      })),
    [publisherData]
  );
  const canSubmit = Boolean(selectedType && selectedOrg && userName.trim() && email.trim());

  useEffect(() => {
    if (mode === 'edit' && selectedUser) {
      setUserName(selectedUser.userAlias);
      setEmail(selectedUser.emailAddress);
      setSelectedType(
        userTypeOptions.find((option) => option.value === selectedUser.userType) ?? {
          label: selectedUser.userType,
          value: selectedUser.userType
        }
      );
      setSelectedOrg(orgOptions.find((option) => option.value === selectedUser.orgId) ?? {
        label: selectedUser.orgId,
        value: selectedUser.orgId
      });
      setSelectedDemandPartners(
        selectedUser.dpList
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
          .map(
            (value) =>
              demandPartnerOptions.find((option) => option.value === value) ?? {
                label: value,
                value
              }
          )
      );
      setSelectedPublishers([]);
      return;
    }

    setUserName('');
    setEmail('');
    setSelectedDemandPartners([]);
    setSelectedPublishers([]);
    setSelectedType(null);
    setSelectedOrg(tokenOrgId ? { label: tokenOrgId, value: tokenOrgId } : null);
  }, [demandPartnerOptions, mode, orgOptions, selectedUser, tokenOrgId, userTypeOptions]);

  // useEffect(() => {
  //   if (mode !== 'create' || !selectedType) {
  //     return;
  //   }

  //   const isDemandPartnerUser = selectedType.value.startsWith('DP-');
  //   if (currentUserType === userTypes.Crackle_Admin && isDemandPartnerUser) {
  //     setSelectedOrg((currentOrg) => currentOrg ?? null);
  //     return;
  //   }

  //   if (tokenOrgId) {
  //     setSelectedOrg({ label: tokenOrgId, value: tokenOrgId });
  //   }
  // }, [currentUserType, mode, selectedType, tokenOrgId]);

  useEffect(() => {
    if (selectedType?.value === userTypes.Crackle_Admin) {
                    
      setSelectedOrg({ value: "CRACKLE-RTB", label: "Crackle RTB" });
    } else if (selectedType?.value === userTypes.Supply_Partner_Admin) {
      setSelectedOrg({ value: "CRACKLE-SP", label: "Crackle SP" });
    } else if (selectedType?.value === userTypes.Crackle_User) {
      setSelectedOrg({ value: "CRACKLE-RTB", label: "Crackle RTB" });
    }
  },[selectedType])

  return (
    <div
      style={{
        alignItems: 'center',
        background: 'var(--app-overlay)',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: token('space.300'),
        position: 'fixed',
        zIndex: 9998
      }}
    >
      <div
        style={{
          background: token('color.background.input'),
          borderRadius: '20px',
          boxShadow: 'var(--app-shadow-modal)',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: token('space.300'),
          width: 'min(720px, 100%)'
        }}
      >
        <Stack space="space.250">
          <Inline spread="space-between" alignBlock="center">
            <Text size="large" weight="bold">
              {mode === 'edit' ? 'Edit User' : 'Add User'}
            </Text>
            <Button appearance="subtle" onClick={onClose}>
              Close
            </Button>
          </Inline>

          <div
            style={{
              display: 'grid',
              gap: token('space.200'),
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))'
            }}
          >
            <div>
              <Text color="color.text.subtle" size="small">
                User Type
              </Text>
              <Select
                inputId="user-type"
                options={userTypeOptions}
                value={selectedType}
                onChange={(option) => setSelectedType((option as SelectOption | null) ?? null)}
                spacing="compact"
                isDisabled={mode === 'edit'}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div>
              <Text color="color.text.subtle" size="small">
                Org ID
              </Text>
              <Select
                inputId="user-org-id"
                options={orgOptions}
                value={selectedOrg}
                onChange={(option) => {

                  setSelectedOrg((option as SelectOption | null) ?? null)
                }}
                spacing="compact"
                isDisabled={orgSelectDisabled}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div>
              <Text color="color.text.subtle" size="small">
                Name
              </Text>
              <TextField
                value={userName}
                onChange={(event) => setUserName(event.currentTarget.value)}
                isDisabled={mode === 'edit'}
              />
            </div>

            <div>
              <Text color="color.text.subtle" size="small">
                Email
              </Text>
              <TextField
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                isDisabled={mode === 'edit'}
              />
            </div>
          </div>

          {showDemandPartnerList ? (
            <MultiSelectInput
              inputId="user-dp-list"
              label="DP List"
              options={demandPartnerOptions}
              value={selectedDemandPartners}
              onChange={setSelectedDemandPartners}
              placeholder="Select demand partners"
              enableSelectAll
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          ) : null}

          {showPublisherList ? (
            <MultiSelectInput
              inputId="user-publisher-list"
              label="Publisher List"
              options={publisherOptions}
              value={selectedPublishers}
              onChange={setSelectedPublishers}
              placeholder="Select publishers"
              enableSelectAll
              isDisabled={isLoadingPublishers}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          ) : null}

          <Inline space="space.100">
            <Button
              appearance="primary"
              isLoading={isSaving}
              isDisabled={!canSubmit}
              onClick={() => {
                if (!selectedType || !selectedOrg) {
                  return;
                }

                onSave({
                  userName: userName.trim(),
                  email: email.trim(),
                  userType: selectedType,
                  organization: selectedOrg,
                  demandPartners: selectedDemandPartners,
                  publishers: selectedPublishers
                });
              }}
            >
              {mode === 'edit' ? 'Save Changes' : 'Create User'}
            </Button>
            <Button appearance="subtle" isDisabled={isSaving} onClick={onClose}>
              Cancel
            </Button>
          </Inline>
        </Stack>
      </div>
    </div>
  );
}

export default function UserManagementPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { accessToken, userType } = useAppSelector((state) => state.exchange);
  const users = useAppSelector((state) => state.userManagement.users);
  const activeDemandPartners = useAppSelector((state) => state.inventory.activeDemandPartners);
  const tokenOrgId = accessToken ? getOrgIdFromAccessToken(accessToken) : null;
  const currentUserEmail = accessToken ? getEmailFromAccessToken(accessToken) : null;
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(tokenOrgId);
  const [editorMode, setEditorMode] = useState<UserEditorMode>('create');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AccessManagementUser | null>(null);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const isCrackleRole =
    userType === userTypes.Crackle_Admin || userType === userTypes.Crackle_User;
  const isPartnerAdminRole =
    userType === userTypes.Demand_Partner_Admin || userType === userTypes.Supply_Partner_Admin;
  const canManageUsers =
    userType === userTypes.Crackle_Admin ||
    userType === userTypes.Demand_Partner_Admin ||
    userType === userTypes.Supply_Partner_Admin;

  const breadcrumbs = (
    <Breadcrumbs>
      <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
      <BreadcrumbsItem text="User Management" key="User Management" />
    </Breadcrumbs>
  );

  useEffect(() => {
    setSelectedOrgId(tokenOrgId);
  }, [tokenOrgId]);

  const orgOptions = useMemo(() => {
    const orgMap = new Map<string, string>();

    cOrgIDOptions.forEach((option) => {
      orgMap.set(option.value, option.label);
    });

    activeDemandPartners.forEach((partner) => {
      orgMap.set(partner.dpId, partner.dpAlias);
    });

    if (tokenOrgId && !orgMap.has(tokenOrgId)) {
      orgMap.set(tokenOrgId, tokenOrgId);
    }

    return Array.from(orgMap.entries())
      .sort(([leftId, leftLabel], [rightId, rightLabel]) =>
        leftLabel === rightLabel
          ? leftId.localeCompare(rightId)
          : leftLabel.localeCompare(rightLabel)
      )
      .map(([orgId, orgLabel]) => ({
        label: orgLabel,
        value: orgId
      }));
  }, [activeDemandPartners, tokenOrgId]);

  const demandPartnerOptions = useMemo<MultiSelectOption[]>(
    () =>
      activeDemandPartners.map((partner) => ({
        label: partner.dpAlias,
        value: partner.dpId
      })),
    [activeDemandPartners]
  );

  const effectiveOrgId = isCrackleRole ? selectedOrgId : isPartnerAdminRole ? tokenOrgId : null;

  const { data, isLoading } = useQuery({
    queryKey: ['usersByOrgId', effectiveOrgId],
    queryFn: () => getUsersByOrgId(effectiveOrgId ?? ''),
    enabled: Boolean(effectiveOrgId) && canAccessPage(userType, 'userManagement')
  });

  useEffect(() => {
    if (effectiveOrgId && data) {
      dispatch(setUsersForOrg({ orgId: effectiveOrgId, users: data }));
    }
  }, [data, dispatch, effectiveOrgId]);

  const refreshUsers = async (orgId: string) => {
    const refreshedUsers = await getUsersByOrgId(orgId);
    dispatch(setUsersForOrg({ orgId, users: refreshedUsers }));
  };

  const createMutation = useMutation({
    mutationFn: (payload: {
      userName: string;
      email: string;
      userType: SelectOption;
      organization: SelectOption;
      demandPartners: SelectOption[];
      publishers: SelectOption[];
    }) =>
      createAccessManagementUser(
        payload.userName,
        payload.email,
        payload.userType,
        payload.organization,
        payload.demandPartners,
        payload.publishers
      ),
    onSuccess: async (_, variables) => {
      if (variables.organization.value === effectiveOrgId) {
        await refreshUsers(variables.organization.value);
      }
      setIsEditorOpen(false);
      setSelectedUser(null);
    }
  });

  const editMutation = useMutation({
    mutationFn: (payload: { email: string; demandPartners: SelectOption[] }) =>
      editAccessManagementUser(payload.email, payload.demandPartners),
    onSuccess: async () => {
      if (effectiveOrgId) {
        await refreshUsers(effectiveOrgId);
      }
      setIsEditorOpen(false);
      setSelectedUser(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (email: string) => deleteUserByEmail(email),
    onSuccess: async () => {
      if (effectiveOrgId) {
        await refreshUsers(effectiveOrgId);
      }
    }
  });
  const deleteUser = deleteMutation.mutate;
  const isDeletingUser = deleteMutation.isPending;

  const visibleUsers = useMemo(
    () =>
      users
        .filter((user) => user.emailAddress !== currentUserEmail)
        .filter((user) => user.userId !== 'CRK_d1f8n8'),
    [currentUserEmail, users]
  );

  const userColumns = useMemo<ColumnDef<AccessManagementUser, unknown>[]>(
    () => [
      { id: 'userAlias', header: 'Name', accessorKey: 'userAlias', sortingFn: 'alphanumeric' },
      { id: 'userId', header: 'User ID', accessorKey: 'userId', sortingFn: 'alphanumeric' },
      { id: 'emailAddress', header: 'Email', accessorKey: 'emailAddress', sortingFn: 'alphanumeric' },
      { id: 'userType', header: 'User Type', accessorKey: 'userType', sortingFn: 'alphanumeric' },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <Inline space="space.100">
            <Button
              appearance="danger"
              isDisabled={!canManageUsers || isDeletingUser}
              onClick={() => deleteUser(row.original.emailAddress)}
            >
              Delete
            </Button>
            {row.original.userType === userTypes.Crackle_User ? (
              <Button
                appearance="subtle"
                isDisabled={!canManageUsers || isFetchingUser}
                onClick={async () => {
                  setIsFetchingUser(true);
                  try {
                    const user = await getUsersByEmail(row.original.emailAddress);
                    setSelectedUser(user);
                    setEditorMode('edit');
                    setIsEditorOpen(true);
                  } finally {
                    setIsFetchingUser(false);
                  }
                }}
              >
                Edit
              </Button>
            ) : null}
          </Inline>
        )
      }
    ],
    [canManageUsers, deleteUser, isDeletingUser, isFetchingUser]
  );

  if (!canAccessPage(userType, 'userManagement')) {
    return <Navigate to="/no-access" replace />;
  }

  return (
    <>
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              appearance="primary"
              isDisabled={!canManageUsers}
              onClick={() => {
                setSelectedUser(null);
                setEditorMode('create');
                setIsEditorOpen(true);
              }}
            >
              Add user
            </Button>
          }
        >
          User Management
        </PageHeader>

        <Box
          style={{
            background: token('color.background.neutral.subtle'),
            borderRadius: '16px',
            padding: token('space.250')
          }}
        >
          <Text weight="medium">Organization scope</Text>
          <Text color="color.text.subtle">
            Fetching access-managed users for org ID: {effectiveOrgId ?? 'Unavailable'}
          </Text>
          {isCrackleRole ? (
            <div style={{ marginTop: token('space.200'), maxWidth: '320px' }}>
              <Select
                inputId="user-management-org"
                options={orgOptions}
                value={orgOptions.find((option) => option.value === selectedOrgId) ?? null}
                onChange={(option) =>
                  setSelectedOrgId((option as { value: string } | null)?.value ?? null)
                }
                placeholder="Select org ID"
                spacing="compact"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>
          ) : null}
        </Box>

        {isLoading ? (
          <Box
            style={{
              alignItems: 'center',
              display: 'flex',
              height: '240px',
              justifyContent: 'center'
            }}
          >
            <Spinner size="large" />
          </Box>
        ) : (
          <DataTable
            data={visibleUsers}
            columns={userColumns}
            emptyMessage={
              effectiveOrgId ? 'No users found for this organization' : 'Organization not available'
            }
            maxHeight="65vh"
          />
        )}
      </Stack>

      {isEditorOpen ? (
        <UserEditorModal
          mode={editorMode}
          currentUserType={userType}
          tokenOrgId={tokenOrgId}
          orgOptions={orgOptions}
          demandPartnerOptions={demandPartnerOptions}
          selectedUser={selectedUser}
          isSaving={createMutation.isPending || editMutation.isPending}
          onClose={() => {
            setIsEditorOpen(false);
            setSelectedUser(null);
          }}
          onSave={(payload) => {
            if (editorMode === 'edit') {
              editMutation.mutate({
                email: payload.email,
                demandPartners: payload.demandPartners
              });
              return;
            }

            createMutation.mutate(payload);
          }}
        />
      ) : null}
    </>
  );
}
